/**
 * @description Color utility functions for the branding system
 *
 * Key features:
 * - Hex to HSL conversion for CSS custom properties
 * - Auto-generate brand color variants (hover, light, ring) from primary/secondary
 * - Inject CSS custom properties onto document root
 * - WCAG contrast helpers so branded chrome never picks white/black ink blindly
 *
 * @dependencies
 * - constants/brandingDefaults: LYNX_GENERIC_PRIMARY banner fallback
 *
 * @notes
 * - CSS custom properties use HSL channel format (e.g., "239 84% 67%") without hsl() wrapper
 *   to enable Tailwind opacity modifiers like bg-brand/10
 * - Variant generation: hover = darken 10%, light = lightness 93%, ring = 30% opacity
 * - Banner ink is the winner of near-black vs white against the real primary.
 *   Mid-grey must not get white text just because a threshold called it "dark."
 * - Only brand colors are generated — semantic colors (green, red, amber) are untouched
 * - Missing hex falls back to LYNX_GENERIC_PRIMARY, not LynxPay yellow.
 */

import { LYNX_GENERIC_PRIMARY } from '../constants/brandingDefaults';

/** Near-black ink — matches Lynx chrome, not pure #000. */
export const BANNER_INK_DARK = '#0a0a0b';

/** White ink for dark primaries. */
export const BANNER_INK_LIGHT = '#ffffff';

/** WCAG AA for small text. */
export const BANNER_MIN_CONTRAST = 4.5;

/** Lynx/generic overlay primary — used when the tenant hex is missing or invalid. */
const BANNER_FALLBACK_SURFACE = LYNX_GENERIC_PRIMARY;

/**
 * Convert hex color to HSL values
 * @param {string} hex - Hex color (e.g., '#facc15' or '#abc')
 * @returns {{ h: number, s: number, l: number }} HSL values (h: 0-360, s: 0-100, l: 0-100)
 */
export function hexToHSL(hex) {
  let r = 0, g = 0, b = 0;

  // Handle 3-digit hex
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  // Handle 6-digit hex
  else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }

  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
      default:
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

/**
 * Convert HSL values to hex color
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color string
 */
export function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;

  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };

  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Format HSL values as CSS channel string (no hsl() wrapper)
 * @param {{ h: number, s: number, l: number }} hsl
 * @returns {string} e.g., "239 84% 67%"
 */
export function hslToChannels(hsl) {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

/**
 * Generate all brand color variants from primary and secondary hex colors
 * @param {string} primaryHex - Primary brand color (e.g., '#4F46E5')
 * @param {string} secondaryHex - Secondary brand color (e.g., '#6366F1')
 * @returns {object} CSS custom property name → HSL channel string
 */
export function generateBrandVariants(primaryHex, secondaryHex) {
  const primary = hexToHSL(primaryHex);
  const secondary = hexToHSL(secondaryHex);

  return {
    '--brand-primary': hslToChannels(primary),
    '--brand-primary-hover': hslToChannels({ ...primary, l: Math.max(0, primary.l - 10) }),
    '--brand-primary-light': hslToChannels({ ...primary, l: 93 }),
    '--brand-secondary': hslToChannels(secondary),
    '--brand-secondary-hover': hslToChannels({ ...secondary, l: Math.max(0, secondary.l - 10) }),
    '--brand-secondary-light': hslToChannels({ ...secondary, l: 93 })
  };
}

/**
 * Parse #RGB / #RRGGBB / RGB / RRGGBB into 0–255 channels.
 *
 * @param {string} hex
 * @returns {{ r: number, g: number, b: number }|null}
 */
export function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let value = hex.trim();
  if (value.startsWith('#')) value = value.slice(1);
  if (value.length === 3) {
    value = value.split('').map((ch) => ch + ch).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16)
  };
}

/**
 * Canonical #rrggbb or null.
 *
 * @param {string} hex
 * @returns {string|null}
 */
export function normalizeHex(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const to = (n) => n.toString(16).padStart(2, '0');
  return `#${to(rgb.r)}${to(rgb.g)}${to(rgb.b)}`;
}

/**
 * sRGB channel (0–255) → linear light.
 *
 * @param {number} channel
 * @returns {number}
 */
function srgbChannelToLinear(channel) {
  const s = channel / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/**
 * WCAG relative luminance (0–1), or null if hex is invalid.
 *
 * @param {string} hex
 * @returns {number|null}
 */
export function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  return (
    0.2126 * srgbChannelToLinear(rgb.r)
    + 0.7152 * srgbChannelToLinear(rgb.g)
    + 0.0722 * srgbChannelToLinear(rgb.b)
  );
}

/**
 * WCAG contrast ratio (≥1). Invalid hex → 0.
 *
 * @param {string} hexA
 * @param {string} hexB
 * @returns {number}
 */
export function contrastRatio(hexA, hexB) {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  if (a == null || b == null) return 0;
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Pick near-black or white by measuring both against the surface.
 * Ties go to dark ink so grey never defaults to white.
 *
 * @param {string} surfaceHex
 * @returns {string} BANNER_INK_DARK | BANNER_INK_LIGHT
 */
export function pickContrastingInk(surfaceHex) {
  const dark = contrastRatio(surfaceHex, BANNER_INK_DARK);
  const light = contrastRatio(surfaceHex, BANNER_INK_LIGHT);
  return dark >= light ? BANNER_INK_DARK : BANNER_INK_LIGHT;
}

/**
 * Nudge surface lightness toward the ink that already won, to clear AA.
 *
 * @param {string} surfaceHex
 * @param {string} inkHex
 * @returns {string}
 */
function nudgeSurfaceTowardInk(surfaceHex, inkHex) {
  const hsl = hexToHSL(surfaceHex);
  if (inkHex === BANNER_INK_DARK) {
    hsl.l = Math.min(100, hsl.l + 4);
  } else {
    hsl.l = Math.max(0, hsl.l - 4);
  }
  return hslToHex(hsl.h, hsl.s, hsl.l);
}

/**
 * Brand-banner palette: primary surface + contrast-safe ink for copy and CTA.
 * Button fill is the same ink as the text so a white chip cannot vanish on grey.
 *
 * @param {string} [primaryHex] - Tenant primary (branding.colors.primaryColor)
 * @returns {{ surface: string, ink: string, buttonBg: string, buttonFg: string }}
 */
export function resolveBrandBannerColors(primaryHex) {
  let surface = normalizeHex(primaryHex) || BANNER_FALLBACK_SURFACE;

  let ink = pickContrastingInk(surface);
  for (let step = 0; step < 16 && contrastRatio(surface, ink) < BANNER_MIN_CONTRAST; step += 1) {
    surface = nudgeSurfaceTowardInk(surface, ink);
    ink = pickContrastingInk(surface);
  }

  if (contrastRatio(surface, ink) < BANNER_MIN_CONTRAST) {
    surface = BANNER_FALLBACK_SURFACE;
    ink = pickContrastingInk(surface);
  }

  const buttonFg = contrastRatio(ink, surface) >= BANNER_MIN_CONTRAST
    ? surface
    : (ink === BANNER_INK_DARK ? BANNER_INK_LIGHT : BANNER_INK_DARK);

  return {
    surface,
    ink,
    buttonBg: ink,
    buttonFg
  };
}

/**
 * Inject brand CSS custom properties onto document.documentElement
 * @param {object} variants - Output from generateBrandVariants
 */
export function injectBrandCSS(variants) {
  const root = document.documentElement;
  for (const [property, value] of Object.entries(variants)) {
    root.style.setProperty(property, value);
  }
}

/**
 * Remove brand CSS custom properties from document.documentElement
 */
export function clearBrandCSS() {
  const root = document.documentElement;
  const properties = [
    '--brand-primary',
    '--brand-primary-hover',
    '--brand-primary-light',
    '--brand-secondary',
    '--brand-secondary-hover',
    '--brand-secondary-light'
  ];
  for (const property of properties) {
    root.style.removeProperty(property);
  }
}
